import React, { useEffect, useRef, useState } from "react";
import {
  Users,
  Gauge,
  FileText,
  DollarSign,
  CreditCard,
  MessageSquare,
  Database,
  ClipboardList,
  UserCog,
  Building2,
  Settings,
  Wrench,
  Info,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { ProgressBar } from "../ProgressBar";
import { useDataTypesByModule, useMigrationModules } from "../../hooks";
import {
  CATEGORY_METADATA,
  DATATYPE_METADATA,
  DEFAULT_CATEGORY_META,
  DEFAULT_DATATYPE_META,
} from "../../utils";
import { NavigationButtons } from "../shared/NavigationButtons";
import { PageHeader } from "@shared/components/PageHeader";

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
  Users,
  Gauge,
  FileText,
  DollarSign,
  CreditCard,
  MessageSquare,
  Database,
  ClipboardList,
  UserCog,
  Building2,
  Settings,
  Wrench,
};

// Category Tab Component
const CategoryTab = ({ category, isActive, onClick, className = "" }) => {
  const IconComponent = category.icon;

  return (
    <button
      onClick={onClick}
      className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${
        isActive
          ? "bg-blue-600 text-white shadow-md"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      } ${className}`}
    >
      <IconComponent size={16} />
      <div className="text-left">
        <div className="font-medium text-sm">{category.label}</div>
        <div
          className={`text-xs ${isActive ? "text-blue-100" : "text-gray-500"}`}
        >
          {category.dataTypesCount} data types
        </div>
      </div>
    </button>
  );
};

// Data Type Card Component
const DataTypeCard = ({ dataType, isSelected, onSelect }) => {
  const IconComponent = iconMap[dataType.icon] || Users;

  return (
    <div
      onClick={() => onSelect(dataType.id)}
      className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-1
        ${
          isSelected
            ? "border-blue-500 bg-blue-50 shadow-md"
            : "border-gray-200 bg-white hover:border-gray-300"
        }
      `}
    >
      <div className="flex flex-col items-start space-y-3">
        <div
          className={`p-2 rounded-lg ${
            isSelected ? "bg-blue-200" : "bg-blue-100"
          }`}
        >
          <IconComponent
            size={24}
            className={isSelected ? "text-blue-700" : "text-blue-600"}
          />
        </div>
        <div>
          <h3
            className={`font-semibold text-base mb-1 ${
              isSelected ? "text-blue-900" : "text-gray-900"
            }`}
          >
            {dataType.title}
          </h3>
          <p
            className={`text-sm leading-relaxed ${
              isSelected ? "text-blue-700" : "text-gray-600"
            }`}
          >
            {dataType.description}
          </p>
        </div>
      </div>

      {/* Mandatory Fields */}
      <div className="mt-4 mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <div className="w-4 h-4 rounded-full bg-yellow-100 flex items-center justify-center">
            <span className="text-yellow-600 text-xs">⚠</span>
          </div>
          <span className="text-sm font-medium text-yellow-700">
            Mandatory Fields
          </span>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <p className="text-sm text-yellow-800">
            {dataType.mandatoryFields.join(", ")}
          </p>
        </div>
      </div>

      {/* System Records Count */}
      <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
        <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center">
          <span className="text-gray-600 text-xs">ℹ</span>
        </div>
        <span>
          {dataType.systemRecords.toLocaleString()}{" "}
          {dataType.title.toLowerCase()} in system
        </span>
      </div>

      {/* Selection Indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4">
          <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
            <div className="w-2 h-2 bg-white rounded-full" />
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const CategorizedDataTypeSelection = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [activeCategoryKey, setActiveCategoryKey] = useState<number | null>(
    null
  );
  const [selectedDataTypeId, setSelectedDataTypeId] = useState<number | null>(
    null
  );
  const hasLogged = useRef(false);

  const { data: modulesData } = useMigrationModules();

  // Fetch data types for active category
  const { data: dataTypesData, isLoading: isLoadingDataTypes } =
    useDataTypesByModule(activeCategoryKey ?? 0, activeCategoryKey !== null);

  useEffect(() => {
    if (
      dataTypesData?.result &&
      dataTypesData.result.length > 0 &&
      selectedDataTypeId === null
    ) {
      setSelectedDataTypeId(dataTypesData.result[0].dataTypeId);
    }
  }, [dataTypesData, selectedDataTypeId]);

  const enrichedCategories = React.useMemo(() => {
    if (!modulesData?.result) return [];

    return modulesData.result.map((module) => {
      const metadata = CATEGORY_METADATA[module.value] || DEFAULT_CATEGORY_META;
      return {
        key: module.key,
        value: module.value,
        type: module.type,
        count: module.count,
        icon: metadata.icon,
        description: metadata.description,
        id: metadata.id,
      };
    });
  }, [modulesData]);

  const enrichedDataTypes = React.useMemo(() => {
    if (!dataTypesData?.result) return [];

    return dataTypesData.result.map((dataType) => {
      const metadata =
        DATATYPE_METADATA[dataType.dataType] || DEFAULT_DATATYPE_META;
      return {
        id: dataType.dataTypeId,
        name: dataType.dataType,
        title: dataType.dataType,
        description: dataType.description,
        icon: metadata.icon,
        mandatoryFields: dataType.mandatoryFields,
        systemRecords: dataType.totalCount,
      };
    });
  }, [dataTypesData, activeCategoryKey]);

  const activeCategory = enrichedCategories.find(
    (cat) => cat.key === activeCategoryKey
  );
  const dataTypesCount = enrichedDataTypes.length;

  useEffect(() => {
    if (!hasLogged.current && state?.operation_type) {
      hasLogged.current = true;
    }
  }, [state?.operation_type]);

  const selectedData = enrichedDataTypes.find(
    (item) => item.id === selectedDataTypeId
  );

  const handleDataTypeSelect = (dataTypeId: number) => {
    setSelectedDataTypeId(dataTypeId);
  };

  const handleContinue = () => {
    if (state.operation_type === 1) {
      // Update existing data flow - go directly to field updation
      if (selectedData) {
        navigate("/onboarding/data-migration/data-field-updation", {
          state: {
            operation_type: state?.operation_type,
            dataType: selectedData.title, // Use title to match existing logic
            categoryKey: selectedDataTypeId,
            mandatoryFields: selectedData.mandatoryFields,
          },
        });
      }
    } else {
      // Upload new data flow - go to upload method selection first
      if (selectedData) {
        navigate("/onboarding/data-migration/upload-method", {
          state: {
            operation_type: state?.operation_type,
            dataType: selectedData.title, // Use title to match existing logic
            categoryKey: selectedDataTypeId,
            mandatoryFields: selectedData.mandatoryFields,
          },
        });
      }
    }
  };
  const helpData = {
    title: "Before you begin",
    points: [
      "Make sure each upload file contains all mandatory fields for the data type you select. Missing required columns will cause validation errors in later steps.",
      "The system accepts CSV files only. Save or export your spreadsheets as .csv before uploading.",
      {
        text: "Suggested independent migration flows:",
        flows: [
          "Plans ➜ Consumers ➜ Meters ➜ Consumer Meter Mapping ➜ Meter Readings",
          "Consumers ➜ Complaints ➜ Payments",
          "Service Orders",
          "Asset Classification ➜ System Types ➜ Facilities ➜ Systems ➜ Networks ➜ Assets",
        ],
      },
    ],
  };

  function DataUploadHelp() {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-blue-900 flex items-center gap-2">
          <Info size={20} className="text-blue-500" /> {helpData.title}
        </h2>

        <ol className="list-decimal ml-6 mt-2 text-sm text-blue-600 space-y-2">
          {helpData.points.map((point, index) => (
            <li key={index}>
              {typeof point === "string" ? (
                point
              ) : (
                <>
                  {point.text}
                  <div className="mt-1">
                    {point.flows.map((flow, idx) => (
                      <p key={idx}>{flow}</p>
                    ))}
                  </div>
                </>
              )}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white">
      <ProgressBar
        currentStep={1}
        totalSteps={7}
        progress={14}
        currentStepName={"Data Selection"}
      />

      <main className="w-full mx-auto px-6 py-8">
        <div className="mb-8">
          <DataUploadHelp />
          <PageHeader
            title="Select Data Type"
            subtitle="Choose the type of data you're uploading. Use the category filters below to find what you need."
          />

          {/* Category Tabs */}
          <div className="mb-8 mt-4">
            <h3 className="font-semibold text-gray-900 mb-4">Categories</h3>
            <div className="flex flex-wrap gap-3">
              {enrichedCategories.map((category) => (
                <CategoryTab
                  key={category.key}
                  category={{
                    ...category,
                    label: category.value,
                    dataTypesCount: category.count,
                  }}
                  isActive={activeCategoryKey === category.key}
                  onClick={() => setActiveCategoryKey(category.key)}
                />
              ))}
            </div>
          </div>

          {/* Selected Category Section */}
          {activeCategory && (
            <div className="mb-6">
              <div className="flex items-center space-x-3 mb-4">
                <activeCategory.icon size={24} className="text-blue-600" />
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {activeCategory.value}
                  </h3>
                  <p className="text-gray-600">{activeCategory.description}</p>
                </div>
              </div>

              {/* Loading data types */}
              {isLoadingDataTypes ? (
                <div className="text-center py-8 text-gray-600">
                  Loading data types...
                </div>
              ) : (
                /* Data Type Cards Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {enrichedDataTypes.map((dataType) => {
                    return (
                      <DataTypeCard
                        key={dataType.id}
                        dataType={dataType}
                        isSelected={selectedDataTypeId === dataType.id}
                        onSelect={handleDataTypeSelect}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <NavigationButtons
          onBack={() => navigate(-1)}
          onContinue={handleContinue}
          canContinue={selectedDataTypeId !== null}
        />
      </main>
    </div>
  );
};

export default CategorizedDataTypeSelection;
